import { useEffect, useEffectEvent } from 'react'
import {
  Outlet,
  createFileRoute,
  redirect,
  useMatch,
} from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { usePostHog } from '@posthog/react'
import { hasPermission, meQueryOptions } from '#/user/me'
import { UnauthorizedError } from '#/api/client'
import { accountsQueryOptions } from '#/api/accounts'
import { AppShell } from '#/components/app-shell'
import { OrgSidebar } from '#/components/sidebars/org-sidebar'
import { AccountSidebar } from '#/components/sidebars/account-sidebar'
import { WorkspaceSidebar } from '#/components/sidebars/workspace-sidebar'
import { UploadQueueProvider } from '#/components/upload/upload-queue'
import { UploadTray } from '#/components/upload/upload-tray'
import { LandingMoment } from '#/components/brand/landing-moment'

export const Route = createFileRoute('/_authed')({
  beforeLoad: async ({ context, location }) => {
    try {
      const [me] = await Promise.all([
        context.queryClient.ensureQueryData(meQueryOptions),
        context.queryClient.ensureQueryData(accountsQueryOptions),
      ])
      return { me }
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        throw redirect({
          to: '/login',
          search: { return_to: location.href },
        })
      }
      throw err
    }
  },
  component: AuthedLayout,
})

function AuthedLayout() {
  const { me } = Route.useRouteContext()
  const { data: accounts } = useSuspenseQuery(accountsQueryOptions)
  const posthog = usePostHog()

  const identify = useEffectEvent(() => {
    posthog.identify(me.email, {
      email: me.email,
      name:
        [me.first_name, me.last_name].filter(Boolean).join(' ') || undefined,
      organisation: me.organisation.name,
    })
  })
  useEffect(() => {
    identify()
  }, [me.email])

  const accountMatch = useMatch({
    from: '/_authed/accounts/$accountId',
    shouldThrow: false,
  })
  const workspaceMatch = useMatch({
    from: '/_authed/accounts/$accountId/workspaces/$workspaceId',
    shouldThrow: false,
  })

  const accountId = accountMatch?.params.accountId
  const workspaceId = workspaceMatch?.params.workspaceId
  // An account id that isn't in the reader's list doesn't scope the shell.
  const scopedAccountId = accounts.some((a) => a.uuid === accountId)
    ? accountId
    : undefined

  // In an account the rail is the account's, and inside one of its workspaces
  // the workspace sub-nav sits beside it. Anywhere else (an unknown account
  // included) the rail is the organisation's.
  const shell = (
    <AppShell
      sidebar={
        scopedAccountId ? (
          <AccountSidebar
            accountId={scopedAccountId}
            canViewInternal={hasPermission(me, 'annwn:internal:read')}
            canConfigureAccount={hasPermission(me, 'annwn:accounts:configure')}
            canReadChats={hasPermission(me, 'annwn:chats:read')}
            canReadCorpus={hasPermission(me, 'annwn:accounts:read')}
          />
        ) : (
          <OrgSidebar
            canManageUsers={hasPermission(me, 'annwn:users:manage')}
          />
        )
      }
      subnav={
        scopedAccountId && workspaceId ? (
          <WorkspaceSidebar
            accountId={scopedAccountId}
            workspaceId={workspaceId}
            canConfigureWorkspace={hasPermission(
              me,
              'annwn:workspaces:configure',
            )}
          />
        ) : undefined
      }
    >
      <Outlet />
    </AppShell>
  )

  return (
    <UploadQueueProvider>
      {shell}
      <UploadTray />
      <LandingMoment
        accountId={scopedAccountId}
        accounts={accounts}
        organisationName={me.organisation.name}
      />
    </UploadQueueProvider>
  )
}
